import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { clearWorkingDrafts } from "../lib/working-draft";
import { DiscardGuardScope } from "../ui/discard-guard-scope";
import { ApplicationShell } from "./application-shell";
import { lazyScreen } from "./lazy-screen";
import { resolveSignIn, type SignInState } from "./session";
import { SIGN_IN_PATH, SignIn } from "./sign-in";

export { ROUTE_PATHS } from "./application-routes";

const CandidatePortal = lazyScreen(() => import("./onboarding-candidate"), "CandidatePortal");

/**
 * The composition root. Feature routes, navigation, overlays and frame layout each own
 * their module; this file resolves identity and chooses which application tree to mount.
 */
export function App() {
  const [state, setState] = useState<SignInState>({ status: "loading" });

  useEffect(() => {
    let current = true;
    resolveSignIn().then((resolved) => {
      if (current) {
        if (resolved.status === "signed_out" || resolved.status === "candidate")
          clearWorkingDrafts();
        setState(resolved);
      }
    });
    return () => {
      current = false;
    };
  }, []);

  return (
    <DiscardGuardScope>
      <Door state={state}>
        {state.status === "signed_in" ? (
          <ApplicationShell state={state} />
        ) : (
          <SignIn state={state} />
        )}
      </Door>
    </DiscardGuardScope>
  );
}

/**
 * Isolates the location subscription from the persistent shell. A quick-view navigation
 * therefore does not rebuild the frame or the page beneath it.
 */
function Door({ state, children }: { readonly state: SignInState; readonly children: ReactNode }) {
  const here = useLocation();
  if (state.status === "candidate" || here.pathname === "/onboarding/candidate") {
    return <CandidatePortal state={state} />;
  }
  return state.status === "signed_in" && here.pathname === SIGN_IN_PATH ? (
    <SignIn state={state} />
  ) : (
    children
  );
}
